import { useState } from "react";
import { Field, Label } from "@headlessui/react";
import { localeShortDateTime } from "../../app/datetimeUtils";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ModalForm from "../../components/ModalForm";
import Password from "../../components/input/Password";
import RequiredField from "../../components/RequiredField";
import { passwordInputClassName, submitAndReset } from "./PasswordConfirmDialog";
import { SecretViewModel, useRestoreSecretMutation } from "./secretsApiSlice";
import { enabledVersions } from "./SecretsTab";

interface Props {
	secret: SecretViewModel;
	onClose: () => void;
}

/**
 * Restore a secret to it's previous value.
 */
export default function RestoreSecretDialog({ secret, onClose }: Props) {
	const [current, previous] = enabledVersions(secret);
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [restoreSecret, { reset: resetRestore }] = useRestoreSecretMutation();

	function handleConfirm() {
		setError("");
		setIsSubmitting(true);
		submitAndReset(restoreSecret({ secret: secret.name, password }).unwrap(), resetRestore)
			.then(() => onClose())
			.catch((message: string) => {
				setPassword("");
				setError(message);
			})
			.finally(() => setIsSubmitting(false));
	}

	return (
		<ModalForm
			open
			title={`Restore ${secret.name}`}
			form={
				<div className="pt-2 space-y-4">
					<AlertMessage
						type={AlertLevel.Error}
						message={error}
						showIcon
						onClear={() => setError("")}
					/>

					<>
						<p className="text-sm text-gray-600 mb-2">
							{secret.name} will go back to the value for the version below. The
							current value is kept, so this can be undone the same way.
						</p>
						<div className="text-sm grid grid-cols-2 p-3 gap-y-2 border-2 rounded-md shadow-sm border-gray-900/20">
							<div className="flex col-span-2">
								<p className="font-medium text-sm mr-4">Secret: </p>
								<p>{secret.name}</p>
							</div>
							<div className="flex col-span-2">
								<p className="font-medium text-sm mr-4">Version: </p>
								<p>{previous.version}</p>
							</div>
							<div>
								<p className="font-medium text-sm">Created:</p>
								<p>
									{previous.createdOn
										? localeShortDateTime(previous.createdOn)
										: "—"}
								</p>
							</div>
							<div>
								<p className="font-medium text-sm">Used Until:</p>
								<p>
									{current.createdOn
										? localeShortDateTime(current.createdOn)
										: "—"}
								</p>
							</div>
							<div className="flex col-span-2">
								<p className="font-medium text-sm mr-4">Set By:</p>
								<p>
									{!previous.updatedBy
										? "outside MI Write"
										: previous.updatedByFullName
											? `${previous.updatedByFullName} (${previous.updatedBy})`
											: previous.updatedBy}
								</p>
							</div>
						</div>

						<Field>
							<Label htmlFor="secrets-your-password" className="font-medium text-sm">
								Enter Your Password
								<RequiredField />
							</Label>
							<Password
								id="secrets-your-password"
								name="secrets-your-password"
								placeholder="your password"
								autoComplete="new-password"
								className={passwordInputClassName}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
						</Field>
					</>
				</div>
			}
			confirmLabel="Restore Previous Value"
			disableConfirm={!password || isSubmitting}
			onConfirm={handleConfirm}
			onCancel={onClose}
		/>
	);
}
